import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Building2, LayoutDashboard, Users, Receipt, FileText, CreditCard, LogOut, HomeIcon, BarChart3, Landmark, Settings as SettingsIcon, LineChart, Wrench, HardHat, Inbox, Puzzle, Users2, Scale, Activity, CalendarCheck, HelpCircle, Archive, Mail, Library, Wallet, MessageSquare, CalendarDays, Briefcase, ScrollText, Banknote, Menu, X, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import api, { API } from "../../lib/api";
import ProductTour from "../ProductTour";
import PfsNotificationsBell from "../PfsNotificationsBell";
import MentionsBell from "../MentionsBell";
import WorkspaceSwitcher from "../WorkspaceSwitcher";
import ImpersonationBanner from "../ImpersonationBanner";

const landlordNav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
  { section: "Workspace", to: "/dashboard/schedule", label: "Appointments", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/dashboard/mail/inbox", label: "Mail", icon: Mail, testid: "nav-mail" },
  { to: "/dashboard/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", badgeKey: "messages_unread", proOnly: true },
  { to: "/dashboard/payments", label: "Rental Payments", icon: Banknote, testid: "nav-payments" },
  { section: "Portfolio", to: "/dashboard/properties", label: "Properties", icon: Building2, testid: "nav-properties" },
  { to: "/dashboard/tenants", label: "Tenants", icon: Users, testid: "nav-tenants" },
  { to: "/dashboard/leases", label: "Leases", icon: FileText, testid: "nav-leases" },
  { to: "/dashboard/leases/vault", label: "Lease Vault", icon: Archive, testid: "nav-lease-vault" },
  { section: "Operations", to: "/dashboard/vendors", label: "Vendors", icon: HardHat, testid: "nav-vendors" },
  { to: "/dashboard/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/dashboard/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
  { section: "Finance", to: "/dashboard/loans", label: "Loans", icon: Landmark, testid: "nav-loans", badgeKey: "loan_alerts" },
  { to: "/dashboard/pfs", label: "Financial Statement", icon: Wallet, testid: "nav-pfs" },
  { to: "/dashboard/comps", label: "CRE Comps", icon: Library, testid: "nav-comps" },
  { to: "/dashboard/valuation", label: "Property Valuations", icon: Scale, testid: "nav-valuation" },
  // Hidden by user request — keeping route mounted in App.js for now
  // { to: "/dashboard/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
  { section: "Insights", to: "/dashboard/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/dashboard/performance", label: "Performance", icon: Activity, testid: "nav-performance" },
  { section: "Account", to: "/dashboard/workspaces", label: "Workspaces", icon: Briefcase, testid: "nav-workspaces" },
  { to: "/dashboard/billing", label: "Subscription Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/dashboard/help", label: "Help & Manual", icon: HelpCircle, testid: "nav-help" },
];

const adminExtraNav = [
  { section: "Capital Raise", to: "/dashboard/deals", label: "Deals", icon: Briefcase, testid: "nav-deals" },
  { section: "Platform", to: "/dashboard/admin/revenue", label: "Platform Revenue", icon: LineChart, testid: "nav-admin-revenue" },
  { to: "/dashboard/admin/marketing", label: "Marketing Kit", icon: Sparkles, testid: "nav-admin-marketing" },
  { to: "/dashboard/admin/blog", label: "Blog Editor", icon: FileText, testid: "nav-admin-blog" },
  { to: "/dashboard/admin/demo-requests", label: "Demo Requests", icon: CalendarCheck, testid: "nav-admin-demo-requests" },
  { to: "/dashboard/admin/landlords", label: "Platform Users", icon: Users2, testid: "nav-admin-landlords" },
  { to: "/dashboard/admin/audit-log", label: "Audit Log", icon: ScrollText, testid: "nav-admin-audit-log" },
  { to: "/dashboard/admin/integrations", label: "Integrations", icon: Puzzle, testid: "nav-admin-integrations" },
];

const tenantNav = [
  { to: "/tenant", label: "My Portal", icon: HomeIcon, testid: "nav-tenant-portal" },
  { to: "/tenant/help", label: "Help & Manual", icon: HelpCircle, testid: "nav-tenant-help" },
];

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Collapsible sidebar sections — preference persisted to localStorage so it
  // survives page reloads. Each section renders a chevron header; clicking it
  // toggles. Items inside a collapsed section are hidden but still routable.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rv_nav_collapsed") || "{}");
    } catch {
      return {};
    }
  });
  const toggleSection = (name) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      try {
        localStorage.setItem("rv_nav_collapsed", JSON.stringify(next));
      } catch {
        /* quota — ignore */
      }
      return next;
    });
  };
  // Close the mobile drawer whenever the route changes so a nav click dismisses it.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  // `loans_module_enabled` defaults to ON for landlords who haven't set it.
  // Hide the Loans nav item when the landlord has turned it off in Settings.
  const loansEnabled = user?.loans_module_enabled !== false;
  // Personal Financial Statement is included natively on Pro/Enterprise
  // plans AND available as a $20/mo add-on on Starter + Loans Only. Admins
  // always see it (matches the backend `require_pfs_access` gate).
  const userPlan = (user?.subscription_plan || "").toLowerCase();
  const pfsEnabled =
    user?.role === "admin"
    || userPlan === "pro" || userPlan === "enterprise"
    || userPlan === "fund_manager" || userPlan === "enterprise_multi"
    || user?.pfs_addon === true;
  const messagesEnabled = user?.role === "admin" || userPlan === "pro" || userPlan === "enterprise";
  // "Loans Only" subscribers don't manage properties / tenants / leases. Strip
  // every nav entry that isn't directly part of the loan-stack workflow so the
  // sidebar stays focused (admins always see the full nav for QA).
  const loansOnly = userPlan === "loans_only" && user?.role !== "admin";
  // Loans Only users who buy the PFS add-on get the PFS page in addition.
  const LOANS_ONLY_ALLOWED = new Set([
    "/dashboard",
    "/dashboard/loans",
    "/dashboard/reports",
    "/dashboard/billing",
    "/dashboard/settings",
    "/dashboard/help",
    ...(user?.pfs_addon ? ["/dashboard/pfs"] : []),
  ]);
  let baseNav = landlordNav;
  if (loansOnly) {
    baseNav = baseNav.filter((n) => LOANS_ONLY_ALLOWED.has(n.to));
  }
  if (!loansEnabled) {
    baseNav = baseNav.filter((n) => n.to !== "/dashboard/loans");
  }
  if (!pfsEnabled) {
    baseNav = baseNav.filter((n) => n.to !== "/dashboard/pfs");
  }
  // Keep Messages always visible for landlord/admin — page itself shows the upgrade gate.
  const nav = user?.role === "tenant"
    ? tenantNav
    : (user?.role === "admin" ? [...baseNav, ...adminExtraNav] : baseNav);
  const consoleLabel =
    user?.role === "admin" ? "Admin Console"
    : user?.role === "tenant" ? "Tenant Portal"
    : "Owner Console";

  // Live unread message badge (Pro/Enterprise + Admin only).
  const [messagesUnread, setMessagesUnread] = useState(0);
  useEffect(() => {
    if (!messagesEnabled || user?.role === "tenant") return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await api.get("/messages/unread-count");
        if (!cancelled) setMessagesUnread(res.data?.unread || 0);
      } catch {
        // silent
      }
    };
    fetchOnce();
    // Slow polling fallback in case SSE disconnects
    const intv = setInterval(fetchOnce, 30000);
    // Realtime via SSE so the badge updates the instant a message arrives
    const tok = localStorage.getItem("rv_token");
    let es = null;
    if (tok) {
      try {
        es = new EventSource(`${API}/messages/stream?token=${encodeURIComponent(tok)}`);
        es.addEventListener("snapshot", (ev) => {
          try {
            const d = JSON.parse(ev.data);
            if (!cancelled) setMessagesUnread(d.unread || 0);
          } catch { /* noop */ }
        });
        es.addEventListener("message", () => {
          // A new message landed; just re-fetch the count for accuracy.
          fetchOnce();
        });
      } catch {
        // ignore
      }
    }
    return () => {
      cancelled = true;
      clearInterval(intv);
      if (es) es.close();
    };
  }, [messagesEnabled, user?.role]);

  // Live loan-maturity alert badge — surfaces red-zone urgency (loans
  // maturing in ≤30 days) on the Loans sidebar item so the investor never
  // misses a refi window. We poll every 5 minutes since alert thresholds
  // only flip on calendar boundaries (no need for realtime SSE).
  const [loanAlerts, setLoanAlerts] = useState({ red: 0, yellow: 0, total: 0 });
  useEffect(() => {
    if (user?.role !== "landlord" && user?.role !== "admin") return;
    if (!loansEnabled) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await api.get("/loans/alerts");
        if (cancelled) return;
        setLoanAlerts({
          red: res.data?.counts?.red || 0,
          yellow: res.data?.counts?.yellow || 0,
          total: res.data?.total || 0,
        });
      } catch {
        // silent — badge just stays zero
      }
    };
    fetchOnce();
    const intv = setInterval(fetchOnce, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intv);
    };
  }, [user?.role, loansEnabled]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <ImpersonationBanner />
      {/* Mobile top bar — visible only below md. Has hamburger + brand + minimal actions. */}
      <div
        className="md:hidden sticky top-0 z-40 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4"
        data-testid="shell-mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation menu"
          data-testid="shell-mobile-open-nav"
          className="w-10 h-10 flex items-center justify-center rounded-sm text-slate-700 hover:bg-slate-100"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-slate-900 flex items-center justify-center text-white font-bold text-sm">R</div>
          <div className="font-bold text-slate-900 tracking-tight text-sm" style={{fontFamily:"Outfit"}}>
            RealtyVault <span className="font-light text-slate-500">Manager</span>
          </div>
        </div>
        <div className="w-10" aria-hidden="true" />
      </div>

      {/* Mobile drawer backdrop */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          data-testid="shell-mobile-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-200 z-50 md:z-auto ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="shell-sidebar"
      >
        <div className="px-6 py-6 border-b border-slate-800 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white flex items-center justify-center text-slate-900 font-bold">R</div>
            <div>
              <div className="font-bold text-white tracking-tight" style={{fontFamily:"Outfit"}}>RealtyVault <span className="font-light text-white/50">Manager</span></div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">
                {consoleLabel}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation menu"
            data-testid="shell-mobile-close-nav"
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-sm text-white/60 hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {(user?.role === "landlord" || user?.role === "admin") && <WorkspaceSwitcher />}
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
          {(() => {
            // Track the current section so items without a `section` field
            // inherit the previous section's collapsed state.
            let currentSection = null;
            const out = [];
            nav.forEach((item) => {
              if (item.section) {
                currentSection = item.section;
                const isCollapsed = !!collapsedSections[currentSection];
                out.push(
                  <button
                    key={`section-${item.section}`}
                    type="button"
                    onClick={() => toggleSection(item.section)}
                    className="w-full flex items-center justify-between gap-2 px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-white hover:text-white/70 font-bold transition-colors"
                    data-testid={`nav-section-${item.section.toLowerCase()}`}
                    aria-expanded={!isCollapsed}
                  >
                    <span>{item.section}</span>
                    {isCollapsed ? (
                      <ChevronRight className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                );
              }
              const sectionCollapsed = currentSection && collapsedSections[currentSection];
              if (sectionCollapsed) return; // hide items belonging to a collapsed section
              out.push(
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/dashboard" || item.to === "/tenant" || item.to === "/dashboard/leases"}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors duration-200 ${
                      isActive
                        ? "bg-white text-slate-900 font-medium"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {item.badgeKey === "messages_unread" && messagesUnread > 0 && (
                    <span
                      data-testid="nav-messages-badge"
                      className="bg-rose-600 text-white text-[10px] rounded-full px-2 py-0.5 font-bold leading-none"
                    >
                      {messagesUnread > 99 ? "99+" : messagesUnread}
                    </span>
                  )}
                  {item.badgeKey === "loan_alerts" && loanAlerts.total > 0 && (
                    <span
                      data-testid="nav-loans-badge"
                      title={
                        loanAlerts.red > 0
                          ? `${loanAlerts.red} loan(s) maturing in ≤30 days`
                          : `${loanAlerts.total} loan(s) maturing in ≤90 days`
                      }
                      className={`text-white text-[10px] rounded-full px-2 py-0.5 font-bold leading-none ${
                        loanAlerts.red > 0
                          ? "bg-rose-600"
                          : loanAlerts.yellow > 0
                          ? "bg-amber-500"
                          : "bg-emerald-600"
                      }`}
                    >
                      {loanAlerts.total > 99 ? "99+" : loanAlerts.total}
                    </span>
                  )}
                </NavLink>
              );
            });
            return out;
          })()}
        </nav>
        <div className="shrink-0 p-3 border-t border-slate-800">
          <div className="px-3 py-2 flex items-start gap-2.5">
            <UserAvatar user={user} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white truncate" data-testid="shell-user-name">{user?.full_name}</div>
              <div className="text-xs text-white/50 truncate">{user?.email}</div>
              {user?.role === "landlord" && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-white/50 font-bold">
                  Plan: <span className="text-white">{user?.subscription_plan || "none"}</span>
                </div>
              )}
              {user?.role === "admin" && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-white font-bold">
                  Platform Admin
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
      <main className="md:ml-64 min-h-screen pb-20 md:pb-0">
        {(user?.role === "landlord" || user?.role === "admin") && (
          <div className="sticky top-14 md:top-0 z-30 h-12 bg-white/80 backdrop-blur border-b border-slate-200 flex items-center justify-end gap-3 px-4 md:px-6" data-testid="shell-topbar">
            <MentionsBell enabled={messagesEnabled} />
            <PfsNotificationsBell
              compact
              onOpenLink={({ linkId, snapshotId }) => {
                const qs = new URLSearchParams();
                if (linkId) qs.set("openShareLink", linkId);
                if (snapshotId) qs.set("openSnapshot", snapshotId);
                navigate(`/dashboard/pfs?${qs.toString()}`);
              }}
            />
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-1.5 rounded-sm transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        )}
        {children}
      </main>
      {/* Sticky mobile bottom-tab nav — landlord/admin only, hidden ≥ md.
          Five high-traffic destinations one tap away. */}
      {(user?.role === "landlord" || user?.role === "admin") && (
        <nav
          data-testid="shell-mobile-bottom-nav"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(15,23,42,0.06)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          aria-label="Primary navigation"
        >
          <ul className="grid grid-cols-5 h-16">
            {[
              { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "bottom-nav-dashboard", exact: true },
              { to: "/dashboard/properties", label: "Props", icon: Building2, testid: "bottom-nav-properties" },
              { to: "/dashboard/payments", label: "Payments", icon: Banknote, testid: "bottom-nav-payments" },
              { to: "/dashboard/reports", label: "Reports", icon: BarChart3, testid: "bottom-nav-reports" },
              { to: "/dashboard/schedule", label: "Schedule", icon: CalendarDays, testid: "bottom-nav-schedule" },
            ].map((item) => (
              <li key={item.to} className="flex">
                <NavLink
                  to={item.to}
                  end={item.exact}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold tracking-tight transition-colors ${
                      isActive
                        ? "text-slate-900 bg-slate-50"
                        : "text-slate-500 hover:text-slate-900"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <ProductTour />
    </div>
  );
}

function UserAvatar({ user }) {
  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
  if (user?.avatar_data_uri) {
    return (
      <img
        src={user.avatar_data_uri}
        alt="Profile"
        className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-200 shrink-0"
        data-testid="shell-user-avatar"
      />
    );
  }
  return (
    <div
      className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shrink-0"
      style={{ fontFamily: "Outfit" }}
      data-testid="shell-user-avatar-initials"
    >
      {initials || "?"}
    </div>
  );
}
